import { CreditCardBack } from '@eventbrite/eds-iconography';
import { gettext } from '@eventbrite/i18n';
import {
    DocumentRequestStatusTypes,
    FILL_OUT_TAX_INFORMATION,
    LOCALLY_STORED_TAXPAYER_REQUEST_STATUS_KEY,
    TITLE_PAYOUTS_FROZEN,
    WorkspaceTypes,
} from '../../constants';
import { getLocalStorageItemWithExpiry } from '../../utils';

export const TAXPAYER_SUMMARY_STATUS: {
    [index: string]: any;
} = {
    none: {
        type: 'none',
        modalNotification: false,
        tooltipNotification: false,
        message: {
            title: '',
            subTitle: '',
            titleTooltip: '',
            subTitleTooltip: '',
            buttonText: '',
            buttonLink: '',
        },
    },
    fulfilled: {
        type: 'fulfilled',
        blockNew: false,
        blockEdit: false,
        tooltipNotification: false,
        modalNotification: false,
    },
    wanted: {
        type: 'wanted',
        modalNotification: true,
        tooltipNotification: false,
        message: {
            title: gettext('Complete your taxpayer information'),
            subTitle: gettext(
                'Add your tax information before your sales reach $600 total to avoid delays with your event payouts. US tax law requires us to collect your information because you host paid events.',
            ),
            titleTooltip: '',
            subTitleTooltip: '',
            buttonText: FILL_OUT_TAX_INFORMATION,
            buttonLink: '/organizations/taxpayer-information',
        },
    },
    needed: {
        type: 'needed',
        modalNotification: true,
        tooltipNotification: true,
        message: {
            title: gettext('Add taxpayer information to avoid payout delays'),
            subTitle: gettext(
                'Add your tax information or your payouts may be delayed. US tax law requires us to collect your information because you have at least $600 total in sales.',
            ),
            titleTooltip: '',
            subTitleTooltip: '',
            buttonText: FILL_OUT_TAX_INFORMATION,
            buttonLink: '/organizations/taxpayer-information',
        },
    },
    mismatched: {
        type: 'mismatched',
        modalNotification: true,
        tooltipNotification: true,
        message: {
            title: TITLE_PAYOUTS_FROZEN,
            subTitle: {
                __html: gettext(
                    "According to the IRS, your taxpayer information doesn't match their records. Please update your:<br><ul><li>Name</li><li>identification number</li></ul>You won't receive a payout until your information is updated.",
                ),
            },
            titleTooltip: TITLE_PAYOUTS_FROZEN,
            subTitleTooltip: gettext(
                "Your tax information doesn't match IRS records. Update your information to receive your payouts.",
            ),
            buttonText: gettext('Update Taxpayer information'),
            buttonLink: '/organizations/taxpayer-information',
        },
    },
    locked: {
        type: 'locked',
        modalNotification: true,
        tooltipNotification: true,
        message: {
            title: gettext('Contact payment ops to receive your payouts'),
            subTitle: gettext(
                "According to the IRS, your taxpayer information doesn't match their records for the second time in 3 years. You won't receive a payout until your information is updated correctly. Contact paymentsteam@eventbrite.com to update your information.",
            ),
            titleTooltip: TITLE_PAYOUTS_FROZEN,
            subTitleTooltip: gettext(
                "Your tax information doesn't match IRS records. Contact paymentsteam@eventbrite.com to update your information and resume your payouts.",
            ),
            buttonText: gettext('Contact us'),
            buttonLink: 'mailto:paymentsteam@eventbrite.com',
        },
    },
};

export const ADD_TAX_INFORMATION: { [index: string]: any } = {
    wanted: {
        name: 'addTaxInformation',
        title: gettext('Complete your taxpayer information'),
        message: gettext(
            'Add your tax information before your sales reach $600 total to avoid delays with your event payouts.',
        ),
        buttonText: gettext('Fill out tax information'),
        buttonLink: '/organizations/taxpayer-information',
    },
    needed: {
        name: 'addTaxInformation',
        title: gettext('Add your required taxpayer information'),
        message: gettext(
            'Add your taxpayer information to avoid payout delays. US tax law requires us to collect your information because you have at least $600 total in sales.',
        ),
        buttonText: gettext('Fill out tax information'),
        buttonLink: '/organizations/taxpayer-information',
    },
    locked: {
        name: 'addTaxInformation',
        title: gettext('Complete your taxpayer information'),
        message: gettext(
            "Contact payment ops to receive your payouts. According to the IRS, your taxpayer information doesn't match their records for the second time in 3 years. You won't receive a payout until your information is updated correctly. Contact paymentsteam@eventbrite.com to update your information.",
        ),
        buttonText: gettext('Contact Us'),
        buttonLink: 'mailto:paymentsteam@eventbrite.com',
    },
};

export const getTaxpayerMissingInfo = (
    documentRequestStatus: DocumentRequestStatusTypes,
    workspaceType: string | undefined,
) => {
    const { notificationType, message } =
        TAXPAYER_SUMMARY_STATUS[documentRequestStatus] || {};

    const defaultNotificationObject = {
        showInlineAlert: false,
        notificationType: undefined,
        message: undefined,
        icon: undefined,
    };

    // * When the user submits the taxpayer information form, it takes about 20 min
    // * to the servers to process and reflect the changes in the database.
    // * But the Notification Banner will still ask for the taxpayer information since
    // * [fetchTaxpayerInformationSummary] API call is still returning the old STATUS.
    // *
    // * The Local Storage is used to store the STATUS locally once it was submitted
    // * This information will be removed from the Local Storage after 30 min.
    // * So, for the next 30 minutes the STATUS returned from the API will be ignored.
    // TODO: Don't use local storage with expiry to store taxpayer information status
    // TODO: Handle the taxpayer info status directly from the API without the current delayed time
    const storedStatus = getLocalStorageItemWithExpiry(
        LOCALLY_STORED_TAXPAYER_REQUEST_STATUS_KEY,
    );

    if (
        storedStatus === DocumentRequestStatusTypes.FULFILLED ||
        documentRequestStatus === DocumentRequestStatusTypes.FULFILLED
    ) {
        return defaultNotificationObject;
    }

    return {
        ...defaultNotificationObject,
        notificationType,
        showInlineAlert: workspaceType === WorkspaceTypes.FINANCE,
        icon: CreditCardBack,
        message: {
            title: ADD_TAX_INFORMATION['wanted'].title,
            description: message.title,
            buttonText: message.buttonText,
            buttonLink: message.buttonLink,
            isFullRedirect: message.isFullRedirect,
        },
    };
};
